import { toURLSearchParams, type AwaitedSearchParams } from '@/utils/searchParams/toURLSearchParams';

/**
 * Build the `surface` value the promo-offers api matches on, from a pathname and its
 * query. Both routers (and the client-side refetch) must agree on this exact string, so
 * this is the one place it's assembled:
 *   - `evaluation_date` is a QA-only override for SSR's "now", never part of the surface
 *     the api matches against.
 *   - The trailing slash is dropped (except for `/` itself) so `/sleep` and `/sleep/`
 *     collapse to one surface.
 *   - Params serialize via `URLSearchParams`, which both `qs.stringify` (pages-router) and
 *     `window.location.search` (client) disagree with in their own ways — repeated keys,
 *     space encoding — so only one serialization ever reaches the api.
 *   - Params are sorted by key. SSR receives a query already grouped by key while the
 *     client reads the raw interleaved string, so `?a=1&b=2&a=3` would otherwise serialize
 *     differently on each side.
 */
export function buildSurface(pathname: string, query: AwaitedSearchParams | URLSearchParams): string {
	// Copy a URLSearchParams input so stripping evaluation_date cannot mutate the caller's object.
	const params = query instanceof URLSearchParams ? new URLSearchParams(query) : toURLSearchParams(query);
	params.delete('evaluation_date');
	params.sort();

	const normalizedPathname = pathname !== '/' && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
	const queryString = params.toString();

	return queryString ? `${normalizedPathname}?${queryString}` : normalizedPathname;
}
